import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Search, Plus, Edit2, Power, Loader2, Hash, Download } from 'lucide-react';
import { api, downloadBlob } from '@/api/client';
import { Modal } from '@/components/ui/Modal';
import { PincodeForm } from './PincodeForm';

interface Pin {
  id: number; pincode?: number; zone?: string; billingZone?: string;
  isMetro: boolean; isSpecDest: boolean; isForward: boolean; isPickup: boolean; isReverse?: boolean | null;
  isActive: boolean;
  stateId?: number; cityId?: number;
  /** Who loaded this row. Null on everything imported before the column existed. */
  createdById?: number | null; createdByName?: string | null;
  state?: { id: number; name: string } | null;
  city?: { id: number; name: string } | null;
}
interface State { id: number; name: string }

export function PincodesPanel() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [stateId, setStateId] = useState('');
  const [zone, setZone] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Pin | null>(null);
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const { data: states } = useQuery({ queryKey: ['states'], queryFn: async () => (await api.get<State[]>('/states')).data });
  const { data, isLoading } = useQuery({
    queryKey: ['pincodes', { search, stateId, zone, page }],
    queryFn: async () => {
      const params: any = { page, pageSize };
      if (search) params.search = search;
      if (stateId) params.stateId = stateId;
      if (zone) params.zone = zone;
      return (await api.get<{ items: Pin[]; total: number }>('/pincodes', { params })).data;
    },
    placeholderData: (prev: any) => prev,
  });
  const toggle = useMutation({
    mutationFn: async (id: number) => (await api.patch(`/pincodes/${id}/toggle`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['pincodes'] }); toast.success('Status updated'); },
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 flex-wrap">
          <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2 min-w-[200px]">
            <Search size={14} className="text-slate-400" />
            <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Search by pincode..."
              className="flex-1 bg-transparent outline-none text-sm font-mono" />
          </div>
          <select value={stateId} onChange={(e) => { setStateId(e.target.value); setPage(1); }}
            className="px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-primary min-w-[200px]">
            <option value="">All states</option>
            {states?.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select value={zone} onChange={(e) => { setZone(e.target.value); setPage(1); }}
            className="px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-primary">
            <option value="">All zones</option>
            {['C','C1','E','E1','N','N1','N2','NE','NE2','S','S1','S2','SP1','SP2'].map(z => <option key={z} value={z}>{z}</option>)}
          </select>
        </div>
        <button onClick={() => downloadBlob(`/pincodes/export?${new URLSearchParams({ ...(search ? { search } : {}), ...(stateId ? { stateId } : {}), ...(zone ? { zone } : {}) }).toString()}`, `pincodes-${new Date().toISOString().slice(0,10)}.csv`)}
          className="px-3 py-2 border border-slate-300 text-slate-700 text-sm rounded-lg hover:bg-slate-50 flex items-center gap-2">
          <Download size={14} /> Export
        </button>
        <button onClick={() => setShowCreate(true)} className="bg-primary text-white px-3 py-2 text-sm rounded-lg hover:bg-primary-700 flex items-center gap-2">
          <Plus size={14} /> Add Pincode
        </button>
      </div>

      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-4 py-2 border-b border-slate-100 text-xs text-slate-500 flex justify-between">
          <span>Total: <b>{total.toLocaleString()}</b> pincodes</span>
          <span>
            {((page - 1) * pageSize + 1).toLocaleString()}–{Math.min(page * pageSize, total).toLocaleString()} of {total.toLocaleString()}
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-2 disabled:opacity-30">‹</button>
            <button disabled={page * pageSize >= total} onClick={() => setPage(p => p + 1)} className="px-2 disabled:opacity-30">›</button>
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-3 py-2 w-20">ID</th>
                <th className="text-left px-3 py-2">Pincode</th>
                <th className="text-left px-3 py-2">City</th>
                <th className="text-left px-3 py-2">State</th>
                <th className="text-left px-3 py-2">Zone</th>
                <th className="text-left px-3 py-2">Flags</th>
                <th className="text-left px-3 py-2">Uploaded By</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={9} className="text-center py-8 text-slate-400"><Loader2 size={20} className="mx-auto animate-spin" /></td></tr>}
              {!isLoading && items.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-slate-400"><Hash size={20} className="mx-auto mb-1 opacity-50" />No pincodes found.</td></tr>}
              {items.map(p => (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono text-xs text-slate-500">{p.id}</td>
                  <td className="px-3 py-2 font-mono font-medium">{p.pincode}</td>
                  <td className="px-3 py-2 text-slate-600">{p.city?.name ?? '—'}</td>
                  <td className="px-3 py-2 text-slate-600">{p.state?.name ?? '—'}</td>
                  <td className="px-3 py-2">
                    <span className="text-xs px-1.5 py-0.5 bg-slate-100 rounded font-mono">{p.zone ?? '—'}</span>
                    {p.billingZone && p.billingZone !== p.zone && <span className="text-xs text-slate-400 ml-1">bill: {p.billingZone}</span>}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <div className="flex gap-1 flex-wrap">
                      {p.isMetro && <span className="px-1 bg-violet-50 text-violet-700 rounded">Metro</span>}
                      {p.isSpecDest && <span className="px-1 bg-amber-50 text-amber-700 rounded">SD</span>}
                      {p.isForward && <span className="px-1 bg-blue-50 text-blue-700 rounded">Fwd</span>}
                      {p.isPickup && <span className="px-1 bg-emerald-50 text-emerald-700 rounded">Pick</span>}
                      {p.isReverse && <span className="px-1 bg-orange-50 text-orange-700 rounded">Rev</span>}
                    </div>
                  </td>
                  {/* Blank on every row loaded before the column existed — we genuinely do not know
                      who imported those. A re-import of the sheet now claims them. */}
                  <td className="px-3 py-2 text-slate-600 text-xs">{p.createdByName ?? <span className="text-slate-300">—</span>}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center gap-1 text-xs font-medium ${p.isActive ? 'text-success' : 'text-slate-400'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${p.isActive ? 'bg-success' : 'bg-slate-300'}`} />
                      {p.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <button onClick={() => setEditing(p)} className="p-1.5 text-slate-400 hover:text-primary hover:bg-primary/10 rounded"><Edit2 size={14} /></button>
                    <button onClick={() => toggle.mutate(p.id)}
                      className={`p-1.5 rounded ${p.isActive ? 'text-amber-600 hover:bg-amber-50' : 'text-success hover:bg-success/10'}`}>
                      <Power size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="Add Pincode" maxWidth="lg">
        <PincodeForm onSuccess={() => setShowCreate(false)} />
      </Modal>
      <Modal isOpen={!!editing} onClose={() => setEditing(null)} title="Edit Pincode" subtitle={String(editing?.pincode)} maxWidth="lg">
        {editing && <PincodeForm pincodeId={editing.id} defaultValues={editing} onSuccess={() => setEditing(null)} />}
      </Modal>
    </div>
  );
}
